import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useDebounce } from "use-debounce";
import { ChevronDown, ChevronRight, Info, Loader2 } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTran } from "@/hooks";
import {
  useConsolidatedStore,
  useDisclosureStore,
  useEntryDataStore,
  useDataEntryFiltersStore,
} from "../../hooks/useDataEntryStore";
import {
  loadBreakdown,
  loadConsolidated,
} from "../../operations/consolidated.ops";
import { formatMetricValue } from "../../domains/dataEntry";
import CategoryBadge from "../common/CategoryBadge";
import ReportingPeriodLabel from "../common/ReportingPeriodLabel";
import SourceCell from "../common/SourceCell";
import ConsolidatedBreakdownPanel from "./ConsolidatedBreakdownPanel";
import ConsolidatedEmptyState from "./ConsolidatedEmptyState";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";

const STATUS_DISPLAY = {
  ok: { label: null, tooltip: null },
  missing: {
    label: "Missing",
    tooltip: "No data submitted yet.",
  },
  missing_at_parent: {
    label: "Needs group-level entry",
    tooltip:
      "Sub-organisations have data, but this KPI is reported at group level. Enter the value at the parent company.",
  },
  parent_only_na: {
    label: "n.a",
    tooltip: "This KPI will be presented at group level.",
  },
  qualitative_na: {
    label: "n.a",
    tooltip: "This KPI will be presented at group level.",
  },
  mixed_unit: {
    label: "Mixed units",
    tooltip: "Mixed units across organizations — can't combine.",
  },
  has_remark_only: {
    label: "n.a",
    tooltip: "KPI has been marked as Not Applicable with attached reasoning.",
  },
  undefined: {
    label: "N/A",
    tooltip: "Couldn't calculate (likely division by zero).",
  },
};

// PM xlsx instructs AVERAGE-method headlines carry a hover hint pointing
// the reader to the per-BU breakdown for detail. Same icon affordance as
// the status tooltips above so it reads as "there's more info here".
const METHOD_HOVER = {
  AVERAGE:
    "The average value is presented. For detailed information, please refer to the respective Business Unit.",
};

const ValueCell = ({ row, t }) => {
  const status = row.status || "ok";
  if (status !== "ok") {
    const display = STATUS_DISPLAY[status] || {
      label: status,
      tooltip: "",
    };

    const translatedLabel = display.label ? t(display.label) : display.label;
    const translatedTooltip = display.tooltip ? t(display.tooltip) : "";

    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground italic">
        {translatedLabel}
        {translatedTooltip && (
          <span title={translatedTooltip}>
            <Info size={12} className="opacity-70" />
          </span>
        )}
      </span>
    );
  }

  if (row.value == null) {
    return <span className="text-muted-foreground">—</span>;
  }

  const formatted =
    row.unit === "Year" ? String(row.value) : formatMetricValue(row.value);
  const methodHover = METHOD_HOVER[row.method];

  return (
    <div className="flex items-baseline gap-1 min-w-0">
      <span className="tabular-nums truncate" title={String(formatted)}>
        {formatted}
      </span>
      {row.unit && (
        <span className="text-muted-foreground text-xs shrink-0">
          {row.unit}
        </span>
      )}
      {methodHover && (
        <span title={t(methodHover)} className="shrink-0">
          <Info size={12} className="opacity-70 text-muted-foreground" />
        </span>
      )}
    </div>
  );
};

const findOrgName = (tree, id) => {
  for (const node of tree) {
    if (node.id === id) return node.name;
    if (node.children?.length) {
      const found = findOrgName(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const ConsolidatedTable = observer(() => {
  const { t } = useTran();
  const { disclosures } = useDisclosureStore();
  const { filteredOrgTree } = useEntryDataStore();
  const {
    items,
    loading,
    page,
    pageSize,
    sort,
    expandedRows,
    setPage,
    setPageSize,
    setSort,
    toggleExpanded,
  } = useConsolidatedStore();
  const filters = useDataEntryFiltersStore().filters;
  const orgIdsKey = (filters.orgIds || []).join(",");

  const { reportingYears } = reportingYearStore;
  const handleToggleExpand = (row) => {
    if (!expandedRows.has(row.rowKey)) {
      loadBreakdown(row.rowKey, row.disclosureId, row.year);
    }
    toggleExpanded(row.rowKey);
  };

  const [debouncedSearch] = useDebounce(filters.search, 400);

  const [debouncedFiltersKey] = useDebounce(
    `${filters.year || ""}|${filters.framework || ""}|${filters.category || ""}|${orgIdsKey}|${disclosures.length}`,
    300,
  );

  useEffect(() => {
    loadConsolidated();
  }, [debouncedFiltersKey]);

  // Reset to page 1 whenever a filter that can shrink `total` changes —
  // otherwise being on page 2 of 30 results, then typing a search that
  // narrows to 1 result, leaves `page=2` while pagination shows "Page 1
  // of 1" and the table renders empty.
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filters.metricType,
    filters.year,
    filters.framework,
    filters.category,
    orgIdsKey,
    setPage,
  ]);

  // Hide "missing" rows — a report author wants what they have, not gaps.
  // Other non-ok statuses (mixed_unit, undefined) are kept because the
  // value exists conceptually but couldn't be computed.
  const visibleItems = useMemo(
    () => items.filter((r) => r.status !== "missing"),
    [items],
  );

  // Client-side filters: search across code/title + metric type.
  const filteredItems = useMemo(() => {
    let result = visibleItems;
    if (filters.metricType) {
      result = result.filter((r) => r.source === filters.metricType);
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (r) =>
          String(r.disclosureCode || "")
            .toLowerCase()
            .includes(q) ||
          String(r.disclosureTitle || "")
            .toLowerCase()
            .includes(q),
      );
    }
    return result;
  }, [visibleItems, debouncedSearch, filters.metricType]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const dir = sort.direction === "asc" ? 1 : -1;
    const isMissing = (row) =>
      row[sort.key] == null || (row.status && row.status !== "ok");
    const compare = (av, bv) => {
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av ?? "").localeCompare(String(bv ?? ""));
    };
    sorted.sort((a, b) => {
      const aMissing = isMissing(a);
      const bMissing = isMissing(b);
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      const primary = compare(a[sort.key], b[sort.key]) * dir;
      if (primary !== 0 || sort.key === "year") return primary;
      return compare(a.year, b.year);
    });
    return sorted;
  }, [filteredItems, sort.key, sort.direction]);

  const total = sortedItems.length;
  const pagedItems = sortedItems.slice((page - 1) * pageSize, page * pageSize);

  const showFrameworkColumn = !filters.framework;
  const showYearColumn = !filters.year;

  const columns = useMemo(() => {
    const cols = [
      {
        key: "expand",
        header: "",
        width: "40px",
        render: (row) => {
          const expanded = expandedRows.has(row.rowKey);
          return (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleToggleExpand(row)}
              aria-label={expanded ? t("Collapse") : t("Expand")}
            >
              {expanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </Button>
          );
        },
      },
      {
        key: "index",
        header: "#",
        width: "56px",
        render: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        key: "disclosure",
        header: t("Metric"),
        sortable: true,
        sortKey: "disclosureCode",
        showSortIndicators: true,
        width: "360px",
        render: (row) => (
          <div className="flex items-center gap-1.5 min-w-0">
            <Badge variant="outline" className="font-mono text-xs shrink-0">
              {row.disclosureCode || "—"}
            </Badge>
            <span className="truncate" title={row.disclosureTitle || undefined}>
              {row.disclosureTitle || ""}
            </span>
          </div>
        ),
      },
      {
        key: "category",
        header: t("Category"),
        width: "140px",
        render: (row) => <CategoryBadge category={row.category} />,
      },
    ];

    if (showFrameworkColumn) {
      cols.push({
        key: "framework",
        header: t("Framework"),
        width: "100px",
        render: (row) => (
          <span className="text-muted-foreground">{row.framework || "—"}</span>
        ),
      });
    }

    if (showYearColumn) {
      cols.push({
        key: "year",
        header: t("Reporting Year"),
        sortable: true,
        sortKey: "year",
        showSortIndicators: true,
        width: "200px",
        render: (row) => {
          if (row.year == null) return "-";

          return (
            <ReportingPeriodLabel
              reportingYears={reportingYears}
              year={row.year}
              yearClassName="tabular-nums"
            />
          );
        },
      });
    }

    cols.push({
      key: "value",
      header: t("Result"),
      sortable: true,
      sortKey: "value",
      showSortIndicators: true,
      width: "220px",
      render: (row) => <ValueCell row={row} t={t} />,
    });

    cols.push({
      key: "source",
      header: t("Metric Type"),
      width: "140px",
      render: (row) => <SourceCell source={row.source} />,
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    showFrameworkColumn,
    showYearColumn,
    expandedRows,
    reportingYears,
    t,
  ]);

  const summary = useMemo(() => {
    if (sortedItems.length === 0) return null;
    const ids = filters.orgIds || [];
    let orgName;
    if (ids.length === 0) {
      orgName = t("All Organizations");
    } else if (ids.length === 1) {
      orgName = findOrgName(filteredOrgTree, ids[0]) || t("1 organization");
    } else {
      orgName = t("{{n}} organizations", { n: ids.length });
    }
    let yearLabel;
    if (filters.year) {
      yearLabel = filters.year;
    } else {
      const years = disclosures.flatMap((d) => d.masterDataYears || []);
      yearLabel = years.length
        ? `${Math.min(...years)}–${Math.max(...years)}`
        : t("All Years");
    }
    const total = sortedItems.length;
    const distinctMetrics = new Set(sortedItems.map((r) => r.disclosureId))
      .size;
    return {
      total,
      distinctMetrics,
      isPerYear: total !== distinctMetrics,
      yearLabel,
      orgName,
    };
  }, [
    sortedItems,
    filters.year,
    filters.orgIds,
    filteredOrgTree,
    disclosures,
    t,
  ]);

  if (!loading && disclosures.length === 0) {
    return <ConsolidatedEmptyState />;
  }

  const handleSort = (key) => {
    const direction =
      sort.key === key && sort.direction === "asc" ? "desc" : "asc";
    setSort({ key, direction });
  };

  // Skeleton only on initial load. Filter refetches dim the existing rows
  // via opacity instead of swapping in skeletons mid-flow.
  const initialLoad = loading && items.length === 0;
  const refetching = loading && items.length > 0;

  return (
    <div className="space-y-2">
      {summary && (
        <p className="text-sm text-muted-foreground">
          {t("Showing")}{" "}
          <strong className="text-foreground">{summary.total}</strong>{" "}
          {summary.isPerYear ? (
            <>
              {t("rows for")}{" "}
              <strong className="text-foreground">
                {summary.distinctMetrics}
              </strong>{" "}
              {t("metrics across")}
            </>
          ) : (
            t("metrics for")
          )}{" "}
          <strong className="text-foreground">{summary.yearLabel}</strong>{" "}
          {t("under")}{" "}
          <strong className="text-foreground">{summary.orgName}</strong>.
        </p>
      )}
      <div className="relative">
        {refetching && (
          <div className="pb-3 pointer-events-none sticky top-2 z-10 flex justify-center">
            <div className="rounded-full bg-background shadow-md ring-1 ring-border px-3 py-1.5 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span>{t("Refreshing...")}</span>
            </div>
          </div>
        )}
        <div
          className={cn(
            "overflow-x-auto transition-opacity",
            refetching && "opacity-60",
          )}
        >
          <DataTable
            columns={columns}
            data={pagedItems}
            loading={initialLoad}
            sort={sort}
            onSort={handleSort}
            emptyMessage={t("No consolidated values for this selection.")}
            getRowKey={(row, i) => row.rowKey || `consolidated-${i}`}
            renderExpansion={(row) =>
              expandedRows.has(row.rowKey) ? (
                <ConsolidatedBreakdownPanel breakdownKey={row.rowKey} />
              ) : null
            }
            pagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (size) => setPageSize(size),
              pageSizeOptions: [10, 20, 50],
              disabled: loading,
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default ConsolidatedTable;
